import { GetMenuAppRestaurantDataResponse } from "api-types";
import { AxiosInstance } from "axios";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { parseMenuUrl } from "../menuUrl";
import { useMenuAppRestaurantDataQuery } from "./useMenuAppRestaurantDataQuery";

export type MenuUrlResolutionResult = {
  loading: boolean;
  restaurantData?: GetMenuAppRestaurantDataResponse | undefined;
  menuId?: string;
  urlSlug: string;

  onSelectMenuId: (menuId: string) => void;
};

/**
 * Parser for igfd menu urls.
 *
 * All URLs now resolve via menu-urls-v2 (ADR-011 Phase 7).
 * The city is always sent as empty — for legacy 2-3 segment URLs,
 * the segments are concatenated into the urlSlug (e.g. "london/pizza-place").
 *
 * URL formats:
 *   https://igfd.menu/:urlSlug                        → urlSlug = segment1
 *   https://igfd.menu/:city/:urlSlug                  → urlSlug = city/urlSlug
 *   https://igfd.menu/:city/:urlSlug/:menuId          → urlSlug = city/urlSlug, menuId from path
 *   https://igfd.menu/:urlSlug?menuId=:menuId         → urlSlug = segment1, menuId from query
 */
export function useResolveMenuUrl({
  client,
}: {
  client: AxiosInstance;
}): MenuUrlResolutionResult {
  const location = useLocation();
  const [urlSlug, setUrlSlug] = useState<string>("");
  const [urlParsingComplete, setUrlParsingComplete] = useState<boolean>(false);
  const [menuId, setMenuId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const parsed = parseMenuUrl(location.pathname, location.search);
    setUrlSlug(parsed.urlSlug);
    setMenuId(parsed.menuId);
    setUrlParsingComplete(true);
  }, [location]);

  const { data, isLoading } = useMenuAppRestaurantDataQuery(client, urlSlug);

  const navigate = useNavigate();

  return {
    loading: isLoading || !urlParsingComplete,
    restaurantData: data,
    menuId,
    urlSlug,
    onSelectMenuId: (menuId: string) => {
      const params = new URLSearchParams(location.search);
      params.set("menuId", menuId);
      navigate(`/${urlSlug}?${params.toString()}`);
    },
  };
}
